import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { UserLocale } from 'frontend-utils/models/user';

type TranslationsLocale =
  | 'ja'
  | 'en'
  | 'es'
  | 'de'
  | 'fr'
  | 'pt-br'
  | 'it'
  | 'nl'
  | 'zh-tw';

type LanguageAsset = `translations/${TranslationsLocale}.json`;

export type AssetMap = {
  assets: {
    [languageAsset in LanguageAsset]: string;
  };
};

export default class IntlLoaderService extends Service {
  @service declare intl: Services['intl'];

  private cache = new Map<TranslationsLocale, Record<string, string>>();

  private fetchTranslationTask = task(
    { restartable: true },
    async (language: TranslationsLocale): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { default: resource } = await import(
        `/translations/${language}.json`
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment
      const translations = JSON.parse(resource);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.cache.set(language, translations);
    },
  );

  async load(userLanguage: UserLocale): Promise<void> {
    let language: TranslationsLocale;

    switch (userLanguage) {
      case 'pt-br': {
        language = userLanguage;
        break;
      }

      case 'zh-tw': {
        language = userLanguage;
        break;
      }

      default: {
        // for our translations we just want the language without the country
        language = userLanguage.split('-')[0] as TranslationsLocale;
      }
    }

    if (!this.cache.has(language)) {
      await this.fetchTranslationTask.perform(language);
    }

    const translations = this.cache.get(language)!;

    this.intl.addTranslations(language, translations);
    this.intl.setLocale([language]);
  }
}

declare module '@ember/service' {
  interface Registry {
    'intl-loader': IntlLoaderService;
  }
}
